import { oc } from "@orpc/contract";
import { openapi } from "@orpc/openapi";
import { z } from "zod";
import {
  accountRequiredMessage,
  type ContractService,
  paxRoute,
  sessionRequired,
} from "../../common";
import { ListWaitlistInvitesResponse, RedeemWaitlistInviteParams } from "./schemas";

export const service = {
  group: "waitlist",
  version: "v1alpha1",
  stage: "alpha",
  title: "Pax Waitlist API",
  description:
    "Invite links into Pax. An account waits until an invite or an allowed email lets it in; once in, it has a few invites of its own to hand out.",
} as const satisfies ContractService;

const accountRequired = {
  ...sessionRequired,
  FORBIDDEN: { message: accountRequiredMessage },
} as const;

export const listInvites = oc
  .meta(
    paxRoute({ auth: { kind: "account" }, minimumTier: null, cost: "read" }),
    openapi({
      method: "GET",
      path: "/api/waitlist/v1alpha1/invites",
      operationId: "waitlist.invites.list",
      summary: "List my invites.",
      description:
        "The invite links the caller can hand out, used or not. An account is given three when it's let in.",
      successDescription: "The caller's invites.",
    }),
  )
  .errors(accountRequired)
  .output(ListWaitlistInvitesResponse);

export const redeemInvite = oc
  .meta(
    paxRoute({
      auth: { kind: "account" },
      minimumTier: null,
      cost: "write",
      openToWaitlist: true,
    }),
    openapi({
      method: "POST",
      path: "/api/waitlist/v1alpha1/invites/{code}/redeem",
      operationId: "waitlist.invites.redeem",
      summary: "Use an invite.",
      description:
        "Lets the caller's account in from the waitlist and gives it invites of its own. An invite works once. A guest creates an account first, and an account already in keeps the invite unused.",
      inputStructure: "detailed",
      successStatus: 204,
      successDescription: "The account is in.",
    }),
  )
  .errors({
    ...accountRequired,
    NOT_FOUND: { message: "That invite doesn't exist or has already been used." },
    CONFLICT: { message: "This account is already in, so the invite stays unused." },
  })
  .input(z.object({ params: RedeemWaitlistInviteParams }));
